import { postSignupPhoneOnly } from 'apis/auth';
import { getCreditCardList } from 'apis/payment';
import { getShopInfo, getShopInfoPublic } from 'apis/shop';
import { getWeixinAccessToken, postWeixinOpenId, postWeixinSignToken } from 'apis/weixin';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';

import {
  arsCustomerInfoState,
  bookingModeState,
  cardListState,
  customerState,
  entrySourceState,
  isAuthenticatedState,
  loadingState,
  saveUtmSource,
  shopDetailState,
  shopState,
  utmSourceState,
} from 'store/atoms';

declare global {
  interface Window {
    wx: WeChatMiniProgram;
  }
  interface WeChatMiniProgram {
    miniProgram: {
      navigateTo: (args: { url: string }) => void;
      redirectTo: (args: { url: string }) => void;
      postMessage: (args: { data: any }) => void;
      getEnv: (callback: (res: { miniprogram: boolean }) => void) => void;
    };
    requestpayment: (args: {
      timeStamp: string;
      nonceStr: string;
      package: string;
      signType: string;
      paySign: string;
      success: (res: any) => void;
      fail: (res: any) => void;
    }) => void;
    config: (args: {
      debug: boolean;
      appId: string;
      timestamp: string;
      nonceStr: string;
      signature: string;
      jsApiList: string[];
    }) => void;
  }
}

export function useAppInitialization() {
  const location = useLocation();
  const [shop, setShop] = useRecoilState(shopState);
  const [entrySource, setEntrySource] = useRecoilState(entrySourceState);
  const [cardList, setCardList] = useRecoilState(cardListState);
  const setShopDetail = useSetRecoilState(shopDetailState);
  const [isAuthenticated, setIsAuthenticated] = useRecoilState(isAuthenticatedState);
  const [loading, setLoading] = useRecoilState(loadingState);
  const setArsCustomerInfo = useSetRecoilState(arsCustomerInfoState);
  const setCustomer = useSetRecoilState(customerState);
  const setBookingMode = useSetRecoilState(bookingModeState);
  const setUtmSource = useSetRecoilState(utmSourceState);

  const getWeixinOpenId = async (code: string) => {
    const res = await postWeixinOpenId({
      jsCode: code,
    });
    console.log(res, 'weixin openId ress');
  };
  const weixinConfig = async () => {
    const res: any = await getWeixinAccessToken();
    const tempNonceStr =
      Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    const tempTimestamp = Math.floor(Date.now() / 1000).toString();
    const payload = {
      accessToken: res?.data?.access_token,
      nonceStr: tempNonceStr,
      timestamp: tempTimestamp,
      url: window.location.href.split('#')[0],
    };
    const weixinSign: any = await postWeixinSignToken(payload);
    const signature = weixinSign?.data?.signature;
    window.wx?.config({
      debug: true, // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，可以在pc端打开，参数信息会通过log打出，仅在pc端时才会打印。
      appId: 'wx2ada69631ceb7960', // 必填，服务号的唯一标识
      timestamp: tempTimestamp,
      nonceStr: tempNonceStr,
      signature: signature, // 必填，签名
      jsApiList: [],
    });
  };

  useEffect(() => {
    let cancelled = false;

    const initializeApp = async () => {
      const urlParams = new URLSearchParams(location.search);
      const shopName = urlParams.get('shop');
      const shopId = urlParams.get('shopId');
      const caller = urlParams.get('caller');
      const entrySource = urlParams.get('entrySource');
      const wechatLoginCode = urlParams.get('code');
      const utmSource = urlParams.get('utm_source') || null;

      const hasShopChanged =
        (shopName && shopName !== shop?.shopName) || (shopId && shopId !== shop?.shopId);

      if (hasShopChanged) {
        localStorage.removeItem('kaddieState');
        localStorage.removeItem('customer_token');

        setIsAuthenticated(false);
        setCardList([]);
        setShop(undefined as any);
        setShopDetail(undefined as any);
      }

      // 전화 진입 링크(caller)나 매장이 바뀌는 진입에서만 utm_source를 갱신한다.
      // 앱 안에서 이동해 URL에 utm이 빠져도 그 방문의 값은 유지된다.
      if (caller || hasShopChanged) {
        saveUtmSource(utmSource); // 새로고침 뒤에도 남도록 직접 저장 (atom 주석 참고)
        setUtmSource(utmSource);
      }

      try {
        setLoading(true);

        // shopId 진입 = 국내(콜게이트) 매장, shopName 진입 = 글로벌 매장
        // shopName은 내부 라우팅 시 RouterWrapper가 자동으로 붙이기도 하므로,
        // 매장이 실제로 바뀌는 진입에서만 모드를 갱신한다. (같은 매장 재진입 시 모드 유지)
        if (shopId) {
          setBookingMode('callgate');
        } else if (shopName && (hasShopChanged || !shop)) {
          setBookingMode('global');
        }

        if (shopName || shopId) {
          // shopId가 매장 식별의 소스이므로 항상 shopId를 우선한다.
          // (shopName은 RouterWrapper가 URL에 자동으로 덧붙일 수 있어, 이전 매장명이
          //  남아있으면 엉뚱한 매장을 재조회하는 문제가 생긴다.)
          const lookupParams = shopId ? { shopId } : { shopName: shopName as string };
          const { data } = await getShopInfoPublic(lookupParams);

          setShop(data);

          if (data?.shopSeqNo) {
            const { data: shopDetailData } = await getShopInfo({ shopSeqNo: data.shopSeqNo });
            setShopDetail(shopDetailData);

            const isKorea =
              shopDetailData?.solutionConfig?.global_config?.countryCode === 'KR';

            if (caller && isKorea) {
              try {
                const { data: signupData } = await postSignupPhoneOnly({
                  shopSeqNo: data.shopSeqNo,
                  phone: caller,
                  countryCode: 'KR',
                });
                if (signupData?.customerInfo) {
                  console.log('[ARS] customerInfo:', signupData.customerInfo);

                  if (signupData.authToken) {
                    localStorage.setItem('customer_token', signupData.authToken);
                  }
                  setCustomer(signupData);
                  setIsAuthenticated(true);

                  setArsCustomerInfo({
                    memberId: signupData.memberId,
                    customerId: signupData.customerId,
                    id: signupData.customerInfo?.id,
                    name: signupData.customerInfo?.name,
                    phone: signupData.customerInfo?.phone,
                  });
                }
              } catch (signupError) {
                console.error('[ARS] phone-only signup 실패:', signupError);
              }
            }

          }

          const token = localStorage.getItem('customer_token');

          if (token) {
            setIsAuthenticated(true);
            // 토큰이 있을 때만 카드 정보 가져오기
            const cards = await getCreditCardList();
            if (cancelled) return;
            setCardList(cards);
            if (entrySource === 'wechat') {
              setEntrySource(entrySource);
              //로컬스토리지에 wechatLoginCode 저장
              if (wechatLoginCode && wechatLoginCode !== localStorage.getItem('wechatLoginCode')) {
                localStorage.setItem('wechatLoginCode', wechatLoginCode);
                getWeixinOpenId(wechatLoginCode);
              }
              weixinConfig();
            }
          } else {
            setIsAuthenticated(false);
          }
        }
      } catch (e) {
        console.error('❌ App 초기화 실패:', e);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  return {
    shop,
    loading,
    isAuthenticated,
    pathname: location.pathname,
  };
}
